import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { clinicalMeasurementTypes } from '../../../../configs/clinicalMeasurements';
import { useUser } from '../../../../hooks/useUser';
import { agendaTimezone } from '../../Agenda/agendaUtils';
import { createClinicalMeasurement } from '../../../../services/clinicalMeasurements';

const NewClinicalMeasurementDialog = ({ patientUuid, patientName, onClose }) => {
    const { t } = useTranslation();
    const { user } = useUser();
    const zone = agendaTimezone(user.timezone);
    const [type, setType] = useState(clinicalMeasurementTypes[0].type);
    const [time, setTime] = useState(() => dayjs().tz(zone).format('YYYY-MM-DDTHH:mm'));
    const [values, setValues] = useState({});
    const [notes, setNotes] = useState('');
    const measurement = clinicalMeasurementTypes.find((item) => item.type === type);
    const queryClient = useQueryClient();
    const submitting = useRef(false);
    const mutation = useMutation({
        mutationFn: createClinicalMeasurement,
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ['patient-measurements-v2', patientUuid]
                }),
                queryClient.invalidateQueries({
                    queryKey: ['patient-measurements', patientUuid]
                })
            ]);
            onClose();
        }
    });
    const invalidValue = (field, value) =>
        value !== '' &&
        (!Number.isFinite(Number(value)) ||
            Number(value) < 0 ||
            (field.unit === '%' && Number(value) > 100));
    const hasValues = measurement.fields.some(
        (field) => (values[field.field] ?? '') !== ''
    );
    const validValues = measurement.fields.every(
        (field) => !invalidValue(field, values[field.field] ?? '')
    );
    const validPressure =
        measurement.apiType !== 'M_BLOOD_PRESSURE' ||
        ['systolic', 'diastolic'].every((field) => (values[field] ?? '') !== '');
    const validOxygen =
        measurement.apiType !== 'M_BLOOD_OXYGEN' || (values.spo2 ?? '') !== '';
    const validTime =
        time &&
        dayjs(time).isValid() &&
        dayjs.tz(time, zone).isValid() &&
        !dayjs.tz(time, zone).isAfter(dayjs());
    const canSave = Boolean(
        patientUuid &&
            validTime &&
            hasValues &&
            validValues &&
            validPressure &&
            validOxygen &&
            !mutation.isPending
    );
    const handleSubmit = (event) => {
        event.preventDefault();
        if (!canSave || submitting.current) return;
        submitting.current = true;
        const data = Object.fromEntries(
            measurement.fields
                .filter((field) => (values[field.field] ?? '') !== '')
                .map((field) => [field.field, Number(values[field.field])])
        );
        if (notes.trim() || measurement.apiType === 'M_BLOOD_OXYGEN')
            data.notes = notes.trim();
        mutation.mutate(
            {
                patient: patientUuid,
                ts: dayjs.tz(time, zone).toISOString(),
                dtype: measurement.apiType,
                data
            },
            {
                onSettled: () => {
                    submitting.current = false;
                }
            }
        );
    };
    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            onClose={mutation.isPending ? undefined : onClose}
            disableEscapeKeyDown={mutation.isPending}
            PaperProps={{ component: 'form', onSubmit: handleSubmit }}
            aria-labelledby="new-clinical-measurement-title"
        >
            <DialogTitle id="new-clinical-measurement-title">
                {t('userPage.measurements.newMeasurement')}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {patientName && (
                        <Typography fontWeight={600}>{patientName}</Typography>
                    )}
                    {mutation.isError && (
                        <Alert severity="error">
                            {t('userPage.measurements.saveError')}
                        </Alert>
                    )}
                    <TextField
                        select
                        label={t('userPage.measurements.measurementType')}
                        value={type}
                        disabled={mutation.isPending}
                        onChange={(event) => {
                            setType(event.target.value);
                            setValues({});
                            mutation.reset();
                        }}
                    >
                        {clinicalMeasurementTypes.map((item) => (
                            <MenuItem key={item.type} value={item.type}>
                                {t(item.labelKey)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        type="datetime-local"
                        label={t('userPage.measurements.measuredAt')}
                        value={time}
                        disabled={mutation.isPending}
                        onChange={(event) => setTime(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        error={!validTime}
                        helperText={
                            !validTime
                                ? t('userPage.measurements.invalidMeasurementTime')
                                : undefined
                        }
                    />
                    <Grid container spacing={2}>
                        {measurement.fields.map((field) => {
                            const value = values[field.field] ?? '';
                            const invalid = invalidValue(field, value);
                            return (
                                <Grid
                                    item
                                    xs={12}
                                    sm={measurement.fields.length === 1 ? 12 : 6}
                                    key={field.field}
                                >
                                    <TextField
                                        fullWidth
                                        disabled={mutation.isPending}
                                        required={
                                            (measurement.apiType === 'M_BLOOD_PRESSURE' &&
                                                ['systolic', 'diastolic'].includes(
                                                    field.field
                                                )) ||
                                            (measurement.apiType === 'M_BLOOD_OXYGEN' &&
                                                field.field === 'spo2')
                                        }
                                        label={field.label || t(field.labelKey)}
                                        type="number"
                                        value={value}
                                        onChange={(event) =>
                                            setValues((current) => ({
                                                ...current,
                                                [field.field]: event.target.value
                                            }))
                                        }
                                        inputProps={{
                                            min: 0,
                                            step: 'any',
                                            ...(field.unit === '%' ? { max: 100 } : {})
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {field.unit}
                                                </InputAdornment>
                                            )
                                        }}
                                        error={invalid}
                                        helperText={
                                            invalid
                                                ? t('userPage.measurements.invalidValue')
                                                : undefined
                                        }
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                    <TextField
                        label={t('userPage.measurements.columns.notes')}
                        multiline
                        minRows={3}
                        value={notes}
                        disabled={mutation.isPending}
                        onChange={(event) => setNotes(event.target.value)}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={mutation.isPending}
                >
                    {t('agenda.cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={!canSave}>
                    {t(
                        mutation.isPending
                            ? 'userPage.measurements.saving'
                            : 'agenda.save'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
NewClinicalMeasurementDialog.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    patientName: PropTypes.string,
    onClose: PropTypes.func.isRequired
};
export default NewClinicalMeasurementDialog;
